import React, { useEffect, useRef, useCallback } from "react";
import { Row, Col } from "react-bootstrap";
import { IoClose, IoRemove } from "react-icons/io5";
import { PALETTE } from "../../common/ThemeConstants";
import CustomButton from "../../common/components/CustomButton";

// iOS-style scrollable wheel component
const ScrollableWheel = ({ value, setValue, max, label }) => {
    const wheelRef = useRef(null);
    const isDragging = useRef(false);
    const startY = useRef(0);
    const currentOffset = useRef(0);
    const itemHeight = 50; // Height of each number item
    const visibleItems = 5; // Number of visible items in the wheel

    // Generate array of numbers for the wheel
    const numbers = Array.from({ length: max + 1 }, (_, i) => i);

    // Calculate initial position
    useEffect(() => {
        if (wheelRef.current) {
            // Set initial position based on current value
            // Position the selected value in the center by offsetting by half the container height
            // and then positioning the selected value
            const containerCenter = (itemHeight * visibleItems) / 2;
            const initialOffset = containerCenter - (value * itemHeight) - (itemHeight / 2);
            currentOffset.current = initialOffset;
            wheelRef.current.style.transform = `translateY(${initialOffset}px)`;
        }
    }, [value]);

    // Handle mouse/touch down
    const handleStart = useCallback((e) => {
        isDragging.current = true;
        startY.current = e.clientY || e.touches[0].clientY;
        e.preventDefault();
    }, []);

    // Handle mouse/touch move
    const handleMove = useCallback((e) => {
        if (!isDragging.current) return;

        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        if (!clientY) return;

        const deltaY = clientY - startY.current;
        const newOffset = currentOffset.current + deltaY;

        // Update position
        wheelRef.current.style.transform = `translateY(${newOffset}px)`;
        wheelRef.current.style.transition = 'none';

        startY.current = clientY;
        currentOffset.current = newOffset;

        e.preventDefault();
    }, []);

    // Handle mouse/touch up
    const handleEnd = useCallback(() => {
        if (!isDragging.current) return;
        isDragging.current = false;

        // Calculate the container center point
        const containerCenter = (itemHeight * visibleItems) / 2;

        // Calculate which item should be selected based on current offset
        // We need to account for the container center and item height
        const offsetFromCenter = containerCenter - currentOffset.current;
        const itemIndex = Math.round(offsetFromCenter / itemHeight - 0.5);
        const boundedIndex = Math.max(0, Math.min(max, itemIndex));

        // Calculate the offset that will center the selected item
        const snappedOffset = containerCenter - (boundedIndex * itemHeight) - (itemHeight / 2);

        // Animate to snap position
        wheelRef.current.style.transform = `translateY(${snappedOffset}px)`;
        wheelRef.current.style.transition = 'transform 0.3s ease-out';
        currentOffset.current = snappedOffset;

        // Update value
        setValue(boundedIndex);
    }, [max, setValue]);

    // Set up event listeners
    useEffect(() => {
        const wheel = wheelRef.current;
        if (!wheel) return;

        // Mouse events
        wheel.addEventListener('mousedown', handleStart);
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleEnd);

        // Touch events
        wheel.addEventListener('touchstart', handleStart, { passive: false });
        window.addEventListener('touchmove', handleMove, { passive: false });
        window.addEventListener('touchend', handleEnd);

        return () => {
            // Clean up
            wheel.removeEventListener('mousedown', handleStart);
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleEnd);

            wheel.removeEventListener('touchstart', handleStart);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleEnd);
        };
    }, [handleStart, handleMove, handleEnd]);

    return (
        <div className="d-flex flex-column align-items-center">
            <div
                style={{
                    position: 'relative',
                    height: itemHeight * visibleItems,
                    width: 80,
                    overflow: 'hidden',
                    margin: '10px 0',
                    cursor: 'grab'
                }}
            >
                {/* Top fade gradient */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: itemHeight * 2,
                        background: `linear-gradient(to bottom, ${PALETTE.WHITE}, transparent)`,
                        zIndex: 2,
                        pointerEvents: 'none'
                    }}
                />

                {/* Bottom fade gradient */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: itemHeight * 2,
                        background: `linear-gradient(to top, ${PALETTE.WHITE}, transparent)`,
                        zIndex: 2,
                        pointerEvents: 'none'
                    }}
                />

                {/* Center selection indicator */}
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        right: 0,
                        height: itemHeight,
                        transform: 'translateY(-50%)',
                        backgroundColor: PALETTE.LIGHTGRAY,
                        borderRadius: 10,
                        zIndex: 1,
                        pointerEvents: 'none'
                    }}
                />

                {/* Scrollable wheel */}
                <div
                    ref={wheelRef}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${(itemHeight * visibleItems) / 2 - (value * itemHeight) - (itemHeight / 2)}px)`,
                        transition: 'transform 0.3s ease-out',
                        zIndex: 1
                    }}
                >
                    {numbers.map((num) => (
                        <div
                            key={num}
                            style={{
                                height: itemHeight,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 28,
                                fontWeight: num === value ? 'bold' : 'normal',
                                color: num === value ? PALETTE.DARK : PALETTE.TRUEGRAY,
                                opacity: num === value ? 1 : 0.8
                            }}
                        >
                            {num.toString().padStart(2, '0')}
                        </div>
                    ))}
                </div>
            </div>
            <div className="text-center mt-2" style={{ color: PALETTE.DARKGRAY }}>
                {label}
            </div>
        </div>
    );
};

export default function WorkoutTimer({
    show,
    onClose,
    minutes,
    seconds,
    isRunning,
    isCompleted,
    timeLeft,
    setMinutes,
    setSeconds,
    setIsRunning,
    setIsCompleted,
    handleStartStop,
    handleReset
}) {
    const pulseRef = useRef(null);

    // Start or stop the pulse animation based on isCompleted state
    useEffect(() => {
        if (pulseRef.current) {
            if (isCompleted) {
                pulseRef.current.style.animation = 'pulse 1s infinite';
            } else {
                pulseRef.current.style.animation = 'none';
            }
        }
    }, [isCompleted]);

    if (!show) return null;

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: PALETTE.LIGHTGRAYKINDATRANSPARENT,
                zIndex: 1050,
                display: "flex",
                flexDirection: "column"
            }}
        >

            {/* Backdrop - closes modal when clicked */}
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 1050
                }}
            />

            {/* Content */}
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "0 20px",
                    zIndex: 1051
                }}
            >
                <div
                    style={{
                        backgroundColor: PALETTE.WHITE,
                        borderRadius: 20,
                        padding: 30,
                        width: "100%",
                        maxWidth: 500,
                        boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
                        position: "relative"
                    }}
                    onClick={(e) => e.stopPropagation()} // Prevent clicks from closing the modal
                >
                    {/* Header with title and close button */}
                    <div className="d-flex align-items-center justify-content-between">
                        <div style={{ width: 24 }}></div> {/* Empty space to balance the header */}
                        <p style={{ fontSize: 18, fontWeight: "bold", margin: 0, color: PALETTE.DARK, flex: 1, textAlign: "center" }}>
                            Timer
                        </p>
                        <div
                            style={{
                                cursor: "pointer",
                                width: 24
                            }}
                            onClick={onClose}
                        >
                            {isRunning && timeLeft > 0 ? (
                                <IoRemove size={24} color={PALETTE.TRUEGRAY} />
                            ) : <IoClose size={24} color={PALETTE.TRUEGRAY} />}
                        </div>
                    </div>
                    <Row className="justify-content-center mb-4" ref={pulseRef}>
                        <Col xs="auto" className="d-flex align-items-center">
                            {!isRunning ? (
                                <ScrollableWheel
                                    value={minutes}
                                    setValue={setMinutes}
                                    max={59}
                                    label="Minuti"
                                />
                            ) : (
                                <div
                                    className="d-flex flex-column align-items-center"
                                    style={{ width: 80 }}
                                >
                                    <div
                                        className="d-flex justify-content-center align-items-center"
                                        style={{
                                            fontSize: 48,
                                            fontWeight: "bold",
                                            color: isCompleted ? PALETTE.WHITE : PALETTE.DARK,
                                            width: 80,
                                            height: 80,
                                            margin: "10px 0",
                                            background: isCompleted ? PALETTE.RED : PALETTE.LIGHTGRAY,
                                            borderRadius: 10,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            transition: "background-color 0.3s, color 0.3s"
                                        }}
                                    >
                                        {minutes.toString().padStart(2, '0')}
                                    </div>
                                    <div className="text-center mt-2" style={{ color: PALETTE.DARKGRAY }}>
                                        Minuti
                                    </div>
                                </div>
                            )}
                        </Col>
                        <Col xs="auto" className="d-flex align-items-center">
                            <div
                                style={{
                                    fontSize: 48,
                                    fontWeight: "bold",
                                    height: 250, // Match the height of the wheel container
                                    display: "flex",
                                    alignItems: "center", // Center vertically
                                    justifyContent: "center",
                                    marginTop: -40 // Move the colon up to align with selected numbers
                                }}
                            >
                                :
                            </div>
                        </Col>
                        <Col xs="auto" className="d-flex align-items-center">
                            {!isRunning ? (
                                <ScrollableWheel
                                    value={seconds}
                                    setValue={setSeconds}
                                    max={59}
                                    label="Secondi"
                                />
                            ) : (
                                <div
                                    className="d-flex flex-column align-items-center"
                                    style={{ width: 80 }}
                                >
                                    <div
                                        className="d-flex justify-content-center align-items-center"
                                        style={{
                                            fontSize: 48,
                                            fontWeight: "bold",
                                            color: isCompleted ? PALETTE.WHITE : PALETTE.DARK,
                                            width: 80,
                                            height: 80,
                                            margin: "10px 0",
                                            backgroundColor: isCompleted ? PALETTE.RED : PALETTE.LIGHTGRAY,
                                            borderRadius: 10,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            transition: "background-color 0.3s, color 0.3s"
                                        }}
                                    >
                                        {seconds.toString().padStart(2, '0')}
                                    </div>
                                    <div className="text-center mt-2" style={{ color: PALETTE.DARKGRAY }}>
                                        Secondi
                                    </div>
                                </div>
                            )}
                        </Col>
                    </Row>

                    {/* Footer with buttons */}
                    <Row className="mt-4">
                        <Col xs={6} className="text-center align-self-center m-auto">
                            <CustomButton
                                className="button-no-background"
                                onClick={handleReset}
                                text={"Reset"}
                                maxWidth={200}
                            />
                        </Col>
                        <Col xs={6} className={"text-center align-self-center m-auto"}>
                            <CustomButton
                                text={isRunning ? "Stop" : "Start"}
                                className={isRunning ? "button-danger-outline" : "button-primary-outline"}
                                onClick={handleStartStop}
                                fontWeight="bold"
                                maxWidth={200}
                            />
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
}
